import GUI from "lil-gui";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import waterFragmentShader from "./shaders/water/fragment.glsl";
import waterVertexShader from "./shaders/water/vertex.glsl";

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 });
const debugObject = {};

// Canvas
const canvas = document.querySelector(
  "canvas.webgl",
);

// Scene
const scene = new THREE.Scene();

/**
 * Water
 */
// Geometry
const waterGeometry = new THREE.PlaneGeometry(
  2,
  2,
  512,
  512,
);

// Colors
debugObject.depthColor = "#186691";
debugObject.surfaceColor = "#9BD8FF";

// Material
const waterMaterial = new THREE.ShaderMaterial({
  vertexShader: waterVertexShader,
  fragmentShader: waterFragmentShader,
  uniforms: {
    uTime: { value: 0 },

    uBigWavesElevation: { value: 0.2 },
    uBigWavesFrequency: {
      value: new THREE.Vector2(4, 1.5),
    },
    uBigWavesSpeed: { value: 0.75 },

    uSmallWavesElevation: { value: 0.15 },
    uSmallWavesFrequency: { value: 3.0 },
    uSmallWavesSpeed: { value: 0.2 },
    uSmallIterations: { value: 4.0 },

    uDepthColor: {
      value: new THREE.Color(
        debugObject.depthColor,
      ),
    },
    uSurfaceColor: {
      value: new THREE.Color(
        debugObject.surfaceColor,
      ),
    },
    uColorOffset: { value: 0.515 },
    uColorMultiplier: { value: 3.85 },
  },
});

gui
  .add(
    waterMaterial.uniforms.uBigWavesElevation,
    "value",
  )
  .min(0)
  .max(1)
  .step(0.001)
  .name("uBigWavesElevation");

gui
  .add(
    waterMaterial.uniforms.uBigWavesFrequency
      .value,
    "x",
  )
  .min(0)
  .max(10)
  .step(0.001)
  .name("uBigWavesFrequencyX");

gui
  .add(
    waterMaterial.uniforms.uBigWavesFrequency
      .value,
    "y",
  )
  .min(0)
  .max(10)
  .step(0.001)
  .name("uBigWavesFrequencyY");

gui
  .add(
    waterMaterial.uniforms.uBigWavesSpeed,
    "value",
  )
  .min(0)
  .max(4)
  .step(0.001)
  .name("uBigWavesSpeed");

gui
  .addColor(debugObject, "depthColor")
  .onChange(() => {
    waterMaterial.uniforms.uDepthColor.value.set(
      debugObject.depthColor,
    );
  });

gui
  .addColor(debugObject, "surfaceColor")
  .onChange(() => {
    waterMaterial.uniforms.uSurfaceColor.value.set(
      debugObject.surfaceColor,
    );
  });

gui
  .add(
    waterMaterial.uniforms.uColorOffset,
    "value",
  )
  .min(0)
  .max(1)
  .step(0.001)
  .name("uColorOffset");

gui
  .add(
    waterMaterial.uniforms.uColorMultiplier,
    "value",
  )
  .min(0)
  .max(10)
  .step(0.001)
  .name("uColorMultiplier");

gui
  .add(
    waterMaterial.uniforms.uSmallWavesElevation,
    "value",
  )
  .min(0)
  .max(1)
  .step(0.001)
  .name("uSmallWavesElevation");

gui
  .add(
    waterMaterial.uniforms.uSmallWavesFrequency,
    "value",
  )
  .min(0)
  .max(30)
  .step(0.001)
  .name("uSmallWavesFrequency");

gui
  .add(
    waterMaterial.uniforms.uSmallIterations,
    "value",
  )
  .min(1)
  .max(8)
  .step(1)
  .name("uSmallIterations");

gui
  .add(
    waterMaterial.uniforms.uSmallWavesSpeed,
    "value",
  )
  .min(0)
  .max(4)
  .step(0.001)
  .name("uSmallWavesSpeed");

// Mesh
const water = new THREE.Mesh(
  waterGeometry,
  waterMaterial,
);
water.rotation.x = -Math.PI * 0.5;
scene.add(water);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(
    Math.min(window.devicePixelRatio, 2),
  );
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100,
);
camera.position.set(1, 1, 1);
scene.add(camera);

// Controls
const controls = new OrbitControls(
  camera,
  canvas,
);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(
  Math.min(window.devicePixelRatio, 2),
);

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  // Update water
  waterMaterial.uniforms.uTime.value =
    elapsedTime;

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
